import React, { useState, useEffect } from "react";
import { Box, TextInput, Text, Popover, Textarea } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { DateTime } from "luxon";
import tinycolor from "tinycolor2";

import FilterConditionSelector from "./FilterConditionSelector";

import { useDebounce } from "../../../../hooks/useDebounce";
import { dateFormat, conditions, numberFormat, isUrl } from "../../../../../utils/Filters";
import isSpaceThemed from "../../../../../utils/IsSpaceThemed";
import isDark from "../../../../../utils/IsDarkColor";

export default function FilterTextInput({
  type,
  filter_id,
  section,
  filterFormat = "text",
  value,
  label,
  color,
  selectedOptions,
  colors,
  isEditMode,
  onChange,
  TargetComponent,
  options = {},
  onClearFilter
}) {
  const cond_1 = ["text", "url"].includes(filterFormat);
  const cond_2 = ["number"].includes(filterFormat);
  const cond_3 = ["date"].includes(filterFormat);
  const condValue = cond_1 ? "contains" : cond_2 ? "=" : "is";

  const [opened, setOpened] = useState(false);
  const [isEditing, setIsEditing] = useState(isEditMode);
  const [condition, setCondition] = useState(condValue);

  const [inputValue, setInputValue] = useState(value || "");

  const _color = typeof inputValue === "string" && inputValue.length > 0 ? color || "black" : color || "grey";

  const format = options?.format || "full_date";
  const decimalPlaces = options?.decimal_places || 0;
  const displayDateFormat = dateFormat.find((d) => d.value === format)?.format || "DDD";
  const symbol = filterFormat === "number" ? numberFormat.find((n) => n.value === format)?.symbol || "" : "";

  const showCondition = ["text", "number", "date", "url"].includes(filterFormat);

  const border = isSpaceThemed(colors)
    ? `1px solid ${
      isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(20).toString()
        : tinycolor(colors.background_color).darken(20).toString()
    }`
    : `1px solid #e9ecef`;
  const inputStyles = {
    border,
    backgroundColor: isSpaceThemed(colors) ? colors.background_color : "#FFFFFF",
    color: isSpaceThemed(colors) ? colors.default_text_color : "#000000",
    "&:focus": { border }
  };

  useEffect(() => {
    if (window.$dropdownFilterId && window.$dropdownFilterId === filter_id) {
      setOpened(true);
      window.$dropdownFilterId = null;
    }
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setInputValue(value ? value : Array.isArray(selectedOptions) ? (selectedOptions[0] || "") : "");
    }
  }, [value, selectedOptions, isEditing]);

  useEffect(() => {
    if (section === "space-modal") {
      setIsEditing(isEditMode);
    }
  }, [section, isEditMode]);

  const closePopover = () => {
    setOpened(false);
    setIsEditing(false);
    window.$tiptapSidemenu = false;
  };

  const handleUpdate = (val, newCondition = condition) => {
    if (typeof onChange === "function") {
      onChange(val, showCondition ? newCondition : null);
    }
  };

  const onConditionChange = (item) => {
    setCondition(item.value);
    setInputValue(item.value);
    handleUpdate(["is_empty", "is_not_empty"].includes(item.value) ? "" : inputValue, item.value);
  };

  const renderInput = () => {
    if (["is_empty", "is_not_empty"].includes(condition)) return null;

    switch (filterFormat) {
      case "date":
        return (
          <DatePicker
            value={inputValue ? DateTime.fromISO(inputValue).toJSDate() : null}
            defaultValue={inputValue ? DateTime.fromISO(inputValue).toJSDate() : new Date()}
            withinPortal={false}
            onChange={(date) => {
              const iso = date ? DateTime.fromJSDate(date).toISODate() : "";
              setInputValue(iso);
              debouncedSave(iso);
              // if (type === "profile") {
              //   closePopover();
              // }
            }}
            mt={4}
            mb={8}
            mx={type === "panel" ? 8 : 0}
            size="sm"
            zIndex={99999}
            styles={{ input: { lineHeight: "normal", ...inputStyles } }}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        );
      default:
        const CustomTextInput = filterFormat === "text" && type !== "panel" ? Textarea : TextInput;
        return (
          <CustomTextInput
            value={inputValue}
            autosize
            type={filterFormat === "number" ? "number" : "text"}
            onChange={(e) => {
              setInputValue(e.currentTarget.value);
              debouncedSave(e.currentTarget.value);
            }}
            // onKeyDown={(e) => e.key === "Enter" && closePopover()}
            mt={4}
            mb={8}
            mx={type === "panel" ? 8 : 0}
            styles={{ input: { lineHeight: "normal", ...inputStyles } }}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        );
    }
  };

  const onFocus = () => {
    window.$tiptapSidemenu = true;
  };

  const onBlur = () => {
    window.$tiptapCanFocus = true;
  };

  const renderDisplay = () => {
    if (["is_empty", "is_not_empty"].includes(condition)) {
      return condition.replace(/_/g, " ");
    }
    if (typeof inputValue === "string" && inputValue.length > 0) {
      if (filterFormat === "date") {
        return DateTime.fromISO(inputValue).toFormat(displayDateFormat);
      } else if (filterFormat === "number") {
        return Number(inputValue).toFixed(Number(decimalPlaces));
      } else if (filterFormat === "url") {
        const parts = inputValue.split(/[\s,]+/).filter(Boolean);
        return parts.map((part, partIndex) => {
          const isUrlValue = isUrl(part);
          return (
            <Text
              key={partIndex}
              color={_color}
              lineClamp={type === "card" ? 2 : undefined}
              style={{
                textDecorationLine: isUrlValue ? "underline" : undefined,
                textDecorationColor: isUrlValue ? _color : undefined,
                lineHeight: 1.3,
                cursor: isUrlValue ? "pointer" : undefined
              }}
              onClick={isEditMode || !isUrlValue || section === "list" ? undefined : () => onOpenUrl(part)}
            >
              {cleanUrl(part)}
            </Text>
          );
        });
      }
      return inputValue;
    } else{
      return "Empty";
    }
  };

  const onOpenUrl = (url) => {
    const _url = url.trim();
    window.open(
      /^https?:\/\//i.test(_url) ? _url : `https://${_url}`,
      "_blank"
    );
  };

  const cleanUrl = (url) => {
    return url
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '');
  };

  const debouncedSave = useDebounce((val) => handleUpdate(val), 1000);

  return (
    <Popover
      opened={isEditMode ? opened : false}
      onClose={isEditMode ? closePopover : undefined}
      position="bottom"
      placement="start"
      zIndex={99999}
      spacing={0}
      style={{ width: isEditing ? "100%" : undefined }}
      target={
        TargetComponent ? (
          <TargetComponent onClick={() => setOpened(!opened)} />
        ) : (
          <Box>
            {isEditing ? (
              renderInput()
            ) : (
              <Box
                py={2}
                sx={{
                  cursor: isEditMode ? "pointer" : undefined,
                  "&:hover": isEditMode && { backgroundColor: "lightgrey" },
                  borderRadius: 5
                }}
                onClick={isEditMode ? () => {
                  setIsEditing(true);
                  window.$tiptapSidemenu = true;
                } : undefined}
              >
                <Text
                  component="span"
                  color={_color}
                  size={type === "page" || section === "list" ? "md" : "sm"}
                  lineClamp={type === "card" ? 2 : undefined}
                  style={{ lineHeight: 1.3, wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                >
                  {format !== "percent" ? symbol : ""}
                  {renderDisplay()}
                  {format === "percent" ? symbol : ""}
                </Text>
              </Box>
            )}
          </Box>
        )
      }
    >
      {typeof label === "string" && showCondition && (
        <FilterConditionSelector
          label={label}
          condition={condition}
          conditionData={conditions[filterFormat]}
          onChangeCondition={onConditionChange}
          onClearFilter={onClearFilter}
        />
      )}
      {TargetComponent && !["is_empty", "is_not_empty"].includes(condition) && renderInput()}
    </Popover>
  );
}